import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Button,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { getDoc, doc, deleteDoc, setDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useLocation } from "react-router-dom";
import jsPDF from "jspdf";

const AppointmentRequestFullView = () => {
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState({
    address: "",
    age: "",
    appointmentDate: "",
    appointmentTime: "",
    city: "",
    clinicId: "",
    clinicName: "",
    doctorId: "",
    geohash: "",
    id: "",
    patientName: "",
    phone: "",
    referenceCode: "",
    status: "",
    timestamp: 0,
    title: "",
    tokenNo: "",
    typeOfService: "",
    userId: "",
    whatsappNo: "",
  });
  const [doctorData, setDoctorData] = useState(null); // State to store doctor's information
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uid = params.get("uid");
    if (uid) {
      fetchData(uid);
    }
  }, [location.search]);

  const fetchData = async (uid) => {
    try {
      const docRef = doc(db, "appointmentrequest", uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const appointmentData = docSnap.data();
        setData(appointmentData);

        // Fetch doctor's information
        if (appointmentData.doctorId) {
          const doctorRef = doc(db, "doctors", appointmentData.doctorId);
          const doctorSnap = await getDoc(doctorRef);
          if (doctorSnap.exists()) {
            setDoctorData(doctorSnap.data());
          } else {
            console.log("No such doctor document!");
          }
        }
      } else {
        console.log("No such document!");
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const handleDelete = async () => {
    try {
      const uid = data.id;
      await deleteDoc(doc(db, "appointmentrequest", uid));
      setAlertSeverity("success");
      setAlertMessage("Appointment request deleted successfully!");
      setData({
        address: "",
        age: "",
        appointmentDate: "",
        appointmentTime: "",
        city: "",
        clinicId: "",
        clinicName: "",
        doctorId: "",
        geohash: "",
        id: "",
        patientName: "",
        phone: "",
        referenceCode: "",
        status: "",
        timestamp: 0,
        title: "",
        tokenNo: "",
        typeOfService: "",
        userId: "",
        whatsappNo: "",
      });
      handleDeleteDialogClose();
    } catch (err) {
      console.error("Error deleting appointment request:", err);
      setAlertSeverity("error");
      setAlertMessage(`Error: ${err.message}`);
    } finally {
      setShowAlert(true);
    }
  };

  const handleDeleteDialogOpen = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setDeleteConfirmation("");
  };

  const handleDeleteConfirmationChange = (e) => {
    setDeleteConfirmation(e.target.value);
  };

  const handleAlertClose = () => {
    setShowAlert(false);
  };

  const handleStatusUpdate = async () => {
    try {
      const uid = data.id;
      await setDoc(
        doc(db, "appointmentrequest", uid),
        { ...data, status: newStatus },
        { merge: true }
      );
      setAlertSeverity("success");
      setAlertMessage("Status updated successfully!");
      setShowAlert(true);
    } catch (error) {
      console.error("Error updating status:", error);
      setAlertSeverity("error");
      setAlertMessage(`Error: ${error.message}`);
      setShowAlert(true);
    }
  };


  const handleConvertToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    
    // Add colored header background
    doc.setFillColor(41, 128, 185); // Professional blue color
    doc.rect(0, 0, doc.internal.pageSize.width, 55, 'F');
    
    // Add header with clinic logo and details - now in white color
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255); // White color for header text
    doc.text(doctorData.clinicname, doc.internal.pageSize.width/2, 20, { align: "center" });
    
    // Add doctor details in header - also in white
    doc.setFontSize(12);
    doc.text(`Dr. ${doctorData.doctorName}`, doc.internal.pageSize.width/2, 30, { align: "center" });
    doc.setFontSize(10);
    doc.text(`${doctorData.qualification}`, doc.internal.pageSize.width/2, 35, { align: "center" });
    doc.text(`${doctorData.specialist}`, doc.internal.pageSize.width/2, 40, { align: "center" });
    
    // Add contact details in white
    doc.setFontSize(8);
    doc.text(`${doctorData.address} | Phone: ${doctorData.phone} | Email: ${doctorData.email}`, 
        doc.internal.pageSize.width/2, 45, { align: "center" });
    
    // Add horizontal line - now white
    doc.setDrawColor(0, 119, 185);
    doc.line(10, 50, doc.internal.pageSize.width - 10, 50);
    
    // Rest of the content - reset to dark color
    doc.setTextColor(44, 62, 80);
    
    // Patient details in a box
    doc.setDrawColor(189, 195, 199);
    doc.setFillColor(241, 242, 246);
    doc.rect(10, 60, doc.internal.pageSize.width - 20, 30, 'F');
    
    // ... rest of the existing code remains the same ...
    doc.setFontSize(10);
    doc.text(`Patient Name: ${data.patientName}`, 15, 70);
    doc.text(`Age: ${data.age}`, 15, 75);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, doc.internal.pageSize.width - 60, 70);
    doc.text(`Token No: ${data.tokenNo}`, doc.internal.pageSize.width - 60, 75);
    
    // Add prescription header
    doc.setFontSize(14);
    doc.text("Rx", 20, 110);
    
    // Add horizontal line under Rx
    doc.setDrawColor(44, 62, 80);
    doc.line(20, 115, 40, 115);
    
    // Add footer
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("Doctor's Signature:", pageWidth - 80, pageHeight - 40);
    doc.line(pageWidth - 70, pageHeight - 35, pageWidth - 20, pageHeight - 35);

    // Add digital signature if available
    if (doctorData.signature) {
        doc.addImage(doctorData.signature, 'PNG', 20, doc.internal.pageSize.height - 40, 50, 20);
    }
    
    // Add footer text
    doc.setFontSize(8);
    doc.text("This is a computer-generated prescription by HELLO DOCTOR", doc.internal.pageSize.width/2, 
        doc.internal.pageSize.height - 10, { align: "center" });
    // Modern color combinations (header and footer)
// Option 1: Blue theme
doc.setFillColor(41, 128, 185); // Professional blue

// Option 2: Green theme
doc.setFillColor(46, 204, 113); // Emerald green

// Option 3: Purple theme
doc.setFillColor(142, 68, 173); // Purple

// Option 4: Gradient effect (for header)
const gradient = doc.setFillColor(41, 128, 185);
doc.setGState(new doc.GState({opacity: 0.9}));
    // Save the PDF
    doc.save("prescription.pdf");
};

const handleDownloadInvoice = () => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Header Section
  doc.setFontSize(20);
  doc.setTextColor(44, 62, 80);
  doc.text("MEDICAL INVOICE", pageWidth / 2, 20, { align: "center" });

  // Add Invoice Number and Date
  doc.setFontSize(10);
  doc.text(`Invoice No: INV-${Date.now()}`, pageWidth - 30, 30, { align: "right" });
  doc.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth - 30, 35, { align: "right" });

  let yPos = 45;

  // Create two-column layout for Patient and Doctor details
  doc.setFontSize(12);
  doc.setTextColor(44, 62, 80);

  // Left column - Patient Details
  doc.setFontSize(11);
  doc.text("Bill To:", 15, yPos);
  doc.setFontSize(10);
  doc.text(data.patientName, 15, yPos + 7);
  doc.text(`Phone: ${data.phone}`, 15, yPos + 14);
  doc.text(`Appointment: ${data.appointmentDate}`, 15, yPos + 21);
  doc.text(`Time: ${data.appointmentTime}`, 15, yPos + 28);

  // Right column - Doctor Details
  doc.setFontSize(11);
  doc.text("Doctor Details:", pageWidth - 90, yPos);
  doc.setFontSize(10);
  doc.text(doctorData.doctorName, pageWidth - 90, yPos + 7);
  doc.text(doctorData.clinicname, pageWidth - 90, yPos + 14);

  yPos += 45;

  // Service Details Table
  const tableHeaders = [
    { header: 'No.', width: 20 },
    { header: 'Description', width: 90 },
    { header: 'Qty', width: 20 },
    { header: 'Amount', width: 30 },
    { header: 'Total', width: 30 }
  ];

  // Draw table headers
  doc.setFillColor(44, 62, 80);
  doc.setTextColor(255, 255, 255);
  doc.rect(10, yPos, pageWidth - 20, 10, 'F');

  let xPos = 15;
  tableHeaders.forEach(column => {
    doc.text(column.header, xPos, yPos + 7);
    xPos += column.width;
  });

  // Draw table content
  yPos += 15;
  doc.setTextColor(0, 0, 0);

  // Example items (replace with dynamic data)
  const items = [
    { description: "Consultation", qty: 1, amount: doctorData.fees, total: doctorData.fees },
    { description: "Lab Test", qty: 1, amount: 500, total: 500 },
    { description: "Medication", qty: 2, amount: 200, total: 400 },
  ];


  // Draw table lines
  doc.line(10, yPos - 5, pageWidth - 10, yPos - 5); // Bottom line

  // Total section at the bottom
  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const tax = 0; // Assuming 0% tax for now
  const totalAmount = subtotal + tax;

  yPos += 20;
  doc.setFontSize(11);
  doc.text("Sub Total:",  pageWidth /1.4, pageHeight - 48);

  yPos += 7;
  doc.text("Tax (0%):",  pageWidth / 1.4, pageHeight - 43);

  yPos += 7;
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text("Total Amount:", pageWidth / 1.4, pageHeight - 38);

  // Payment Status
  yPos += 15;
  doc.setFontSize(11);
  doc.setTextColor(0, 150, 0); // Green color for completed status
  doc.text("Payment Status: ",  pageWidth / 1.4, pageHeight - 32);

  // Footer
  doc.setTextColor(128, 128, 128);
  doc.setFontSize(10);
  doc.text("Thank you for choosing our services!", pageWidth / 2, pageHeight - 20, {
    align: "center",
  });


  // Save the PDF
  doc.save("medical_invoice.pdf");
};

  return (
    <div className="App">
      <main style={{ padding: isMobile ? "10px 5px" : "20px 5px" }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={10} md={8} lg={6}>
            <Card style={{ margin: "0 auto" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  color="#272727"
                  align="center"
                  gutterBottom
                >
                  Appointment Request Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Patient Name"
                      name="patientName"
                      value={data.patientName || ""}
                      fullWidth
                      variant="outlined"
                      disabled
                    />
                    <TextField
                      label="Phone"
                      name="phone"
                      value={data.phone || ""}
                      fullWidth
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      disabled
                    />
                    <TextField
                      label="Age"
                      name="age"
                      value={data.age || ""}
                      fullWidth
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      disabled
                    />
                    <TextField
                      label="Address"
                      name="address"
                      value={data.address || ""}
                      fullWidth
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      disabled
                    />
                    <TextField
                      label="City"
                      name="city"
                      value={data.city || ""}
                      fullWidth
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      disabled
                    />
                    <TextField
                      label="Clinic Name"
                      name="clinicName"
                      value={data.clinicName || ""}
                      fullWidth
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      disabled
                    />
                    <TextField
                      label="Doctor ID"
                      name="doctorId"
                      value={data.doctorId || ""}
                      fullWidth
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      disabled
                    />
                    <TextField
                      label="Appointment Date"
                      name="appointmentDate"
                      value={data.appointmentDate || ""}
                      fullWidth
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      disabled
                    />
                    <TextField
                      label="Appointment Time"
                      name="appointmentTime"
                      value={data.appointmentTime || ""}
                      fullWidth
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      disabled
                    />
                    <TextField
                      label="Status"
                      name="status"
                      value={data.status || ""}
                      fullWidth
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      disabled
                    />
                    <TextField
                      label="Token No"
                      name="tokenNo"
                      value={data.tokenNo || ""}
                      fullWidth
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      disabled
                    />
                    <TextField
                      label="Type of Service"
                      name="typeOfService"
                      value={data.typeOfService || ""}
                      fullWidth
                      variant="outlined"
                      style={{ marginTop: "16px" }}
                      disabled
                    />
                  </Grid>

                  {/* Display Doctor Information */}
                  {doctorData && (
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Doctor Information
                      </Typography>
                      <TextField
                        label="Doctor Name"
                        value={doctorData.doctorName || ""}
                        fullWidth
                        variant="outlined"
                        disabled
                      />
                      <TextField
                        label="Clinic Name"
                        value={doctorData.clinicname || ""}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                        disabled
                      />
                      <TextField
                        label="Address"
                        value={doctorData.address || ""}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                        disabled
                      />
                      <TextField
                        label="Phone"
                        value={doctorData.phone || ""}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                        disabled
                      />
                      <TextField
                        label="Email"
                        value={doctorData.email || ""}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                        disabled
                      />
                      <TextField
                        label="Qualification"
                        value={doctorData.qualification || ""}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                        disabled
                      />
                      <TextField
                        label="Experience"
                        value={`${doctorData.experience} years` || ""}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                        disabled
                      />
                      <TextField
                        label="Fees"
                        value={doctorData.fees || ""}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                        disabled
                      />
                      <TextField
                        label="Specialization"
                        value={doctorData.specialist || ""}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                        disabled
                      />
                    </Grid>
                  )}

                  <Grid container justifyContent="center">
                    <Grid item>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px", width: "200px" }}
                      >
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={newStatus}
                          onChange={(e) => setNewStatus(e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="Pending">Pending</MenuItem>
                          <MenuItem value="Rejected">Rejected</MenuItem>
                          <MenuItem value="Completed">Completed</MenuItem>
                          <MenuItem value="Released">Released</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          borderRadius: 15,
                          boxShadow: "none",
                          width: isMobile ? "100%" : "200px",
                          marginTop: "16px",
                          marginLeft: "16px",
                        }}
                        onClick={handleStatusUpdate}
                      >
                        Update Status
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{
                          borderRadius: 15,
                          boxShadow: "none",
                          width: isMobile ? "100%" : "200px",
                          marginTop: "16px",
                          marginLeft: "16px",
                        }}
                        onClick={handleConvertToPDF}
                      >
                        Convert to PDF
                      </Button>
                    </Grid>
                    {data.status === "Completed" && (
                      <Grid item>
                        <Button
                          variant="contained"
                          color="success"
                          sx={{
                            borderRadius: 15,
                            boxShadow: "none",
                            width: isMobile ? "100%" : "200px",
                            marginTop: "16px",
                            marginLeft: "16px",
                          }}
                          onClick={handleDownloadInvoice}
                        >
                          Download Invoice
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <Snackbar
              open={showAlert}
              autoHideDuration={6000}
              onClose={handleAlertClose}
            >
              <Alert
                onClose={handleAlertClose}
                severity={alertSeverity}
                sx={{ width: "100%" }}
              >
                {alertMessage}
              </Alert>
            </Snackbar>
            <Dialog open={showDeleteDialog} onClose={handleDeleteDialogClose}>
              <DialogTitle>Delete Appointment Request</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to delete this appointment request? This
                  action cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteDialogClose} color="primary">
                  Cancel
                </Button>
                <Button onClick={handleDelete} color="secondary">
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </main>
    </div>
  );
};

export default AppointmentRequestFullView;